import ReplayIcon from "@mui/icons-material/Replay";
import { styled, Typography, Box, Stack, Button } from "@mui/material";
import {
  AnimatedCheckMark,
  AnimatedX,
  basicColours,
  ElapsedTimerDisplay,
  mainColours,
  useToast,
  Workstation24Px
} from "@qubit/autoparts";
import { BouncingDotsLoader } from "@qubit/autoparts/src/robotLoader/BouncingDotsLoader";
import { useCallback, memo, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { shallowEqual, useDispatch } from "react-redux";

import { useAppSelector } from "~/app/store";

import { selectPtlSimulationEnabled } from "~/features/devTools/devTools.slice";
import {
  selectPtlDeviceStatusForActivePhase,
  setPtlPhaseNotification,
  setPtlDeviceTestStatus,
  addEvent
} from "~/features/testingTools/testingTools.slice";
import { maxTimeToWaitForPingEventSec } from "~/features/testingTools/testingToolsUtils";
import { getMessageFromRtkError } from "~/lib/rtkErrorToMessage";
import {
  useSimulatePressPTLButtonMutation,
  useStartPtlTestMutation
} from "~/redux/warehouse/testingTools.hook";
import {
  TotePlacementSummaryDto,
  WorkstationPortSummaryDto,
  WorkstationSummaryDto
} from "~/types/api";

import { PTLUserNotificationList } from "./PTLTestWorkflow";

const PTLTotePlacementStyled = styled(Box)<{
  celltype: "port" | "tote" | null;
}>(({ celltype, theme: { palette } }) => ({
  boxSizing: "border-box",
  backgroundColor: palette.background.secondary,
  borderRadius: "10px",
  padding: "6px 10px",
  gap: 0,
  width: "100%",
  height: "100%",
  minWidth: 0,
  minHeight: 0,
  display: "grid",
  gridTemplateRows: "minmax(0, 1fr) minmax(20px, auto)",
  gridTemplateColumns: "1fr minmax(40px, auto)",
  justifyContent: "space-between",
  border: `1px solid ${palette.background.secondary}`,
  ...((celltype === "port" || celltype === null) && {
    justifyContent: "center",
    alignItems: "center",
    ...(celltype === "port"
      ? {
          backgroundColor: palette.primary.main,
          borderColor: palette.primary.main
        }
      : {
          backgroundColor: palette.background.paper
        })
  })
}));

const WorkstationGridCell = ({
  cellId,
  cellData,
  cellPosition,
  selectedWorkstation
}: {
  cellId: string;
  cellData?: WorkstationPortSummaryDto &
    TotePlacementSummaryDto & {
      type: "port" | "tote";
      coordinate: { x: number; y: number };
    };
  cellPosition: {
    x: number;
    y: number;
  };
  selectedWorkstation: WorkstationSummaryDto;
}) => {
  const { t } = useTranslation();
  const { errorToast } = useToast();

  const {
    putToLightModuleId,
    totePosition,
    portId,
    type: cellType
  } = cellData || {};

  const isPortCell = cellType === "port";
  const isToteCell = cellType === "tote";
  const isEmptyCell = !cellType;
  const { x, y } = cellPosition;

  const dispatch = useDispatch();
  const ptlSimulationOptionEnabled = useAppSelector(selectPtlSimulationEnabled);

  const selectStatusForThisCell = useMemo(
    () => selectPtlDeviceStatusForActivePhase(cellData?.putToLightModuleId),
    [cellData?.putToLightModuleId]
  );
  const ptlCellStatus = useAppSelector(selectStatusForThisCell, {
    equalityFn: shallowEqual
  });

  const ptlStatus = ptlCellStatus?.status ?? null;
  const pingStartTime = ptlCellStatus?.startedAt ?? null;

  const showSimulateButton =
    ptlSimulationOptionEnabled && ptlCellStatus && ptlStatus !== "success";

  const [startPtlTest, { isLoading }] = useStartPtlTestMutation();
  const [simulatePtlButtonPress, { isLoading: isSimulatePtlBtnLoading }] =
    useSimulatePressPTLButtonMutation();

  const handleStartPtlTest = async (ptlIdToActivate: number) => {
    try {
      const displaysToActivate = [ptlIdToActivate];
      await startPtlTest({
        displaysToActivate,
        displaysToClean: [],
        gridId: selectedWorkstation.autostoreGridId,
        workstationId: selectedWorkstation.id
      }).unwrap();
      dispatch(
        addEvent({
          text: `PTL test: display ${ptlIdToActivate} activated`,
          state: "Info",
          testOrigin: "PTL"
        })
      );
    } catch (error) {
      dispatch(
        addEvent({
          text: `PTL test: display ${ptlIdToActivate} failed to activate`,
          state: "Failed",
          testOrigin: "PTL"
        })
      );
      errorToast(getMessageFromRtkError(error));
    }
  };

  const simulatePTLButtonPress = async (ptlDeviceId: number) => {
    try {
      await simulatePtlButtonPress({
        deviceId: ptlDeviceId,
        text: "test",
        pickId: "",
        gridId: selectedWorkstation.autostoreGridId
      }).unwrap();
    } catch (error) {
      console.error("Error simulating PTL button press:", error);
    }
  };
  const handlePingTestTimeout = useCallback(() => {
    if (!isToteCell || !putToLightModuleId) return;

    dispatch(
      addEvent({
        text: `PTL test: ping not received for ${putToLightModuleId}`,
        state: "Failed",
        testOrigin: "PTL"
      })
    );
    dispatch(
      setPtlDeviceTestStatus({
        ptlId: putToLightModuleId,
        status: {
          pingOutcome: "error",
          requested: false
        }
      })
    );
    dispatch(
      setPtlPhaseNotification({
        message: (
          <PTLUserNotificationList
            messagesList={[
              t("ptl ping test error device connection"),
              t("ptl ping test error network connection")
            ]}
            messageTitle={t("ptl ping test error title")}
          />
        ),
        type: "error"
      })
    );
  }, [isToteCell, putToLightModuleId, dispatch, t]);

  const renderPingElapsedTime = useCallback(
    (elapsedTimeSec: number) => {
      if (elapsedTimeSec >= maxTimeToWaitForPingEventSec) return null;
      return (
        <Typography
          key={`elapsed-timer-cell-${cellId}`}
          aria-label="elapsed time display"
          role="note"
          color="white"
          variant="body2"
          sx={{
            lineHeight: "1.125rem",
            color: basicColours.mono.white
          }}
          textAlign={"center"}
          gridRow={"2/3"}
        >
          {t("ptl ping test elapsed time", {
            elapsedTime: elapsedTimeSec
          })}
        </Typography>
      );
    },
    [cellId, t]
  );

  return (
    <Box
      id={cellId}
      sx={{
        width: "200px",
        height: "200px",
        display: "grid",
        gridTemplateRows: showSimulateButton ? "1fr auto" : "1fr",
        gap: "2px",
        overflow: "hidden"
      }}
      role="region"
      aria-label={`${cellData?.type} cell container`}
    >
      {ptlStatus === "pending" ? (
        <PTLTotePlacementStyled
          celltype={"port"}
          sx={{
            gridTemplateColumns: "1fr",
            gridTemplateRows: showSimulateButton
              ? "minmax(50px, auto) 20px"
              : "auto",
            rowGap: 1,
            alignContent: "center"
          }}
          role="gridcell"
          aria-label={`${cellData?.type ?? "empty"} cell`}
        >
          <BouncingDotsLoader
            dotStyle={{
              backgroundColor: "white",
              width: "1.125em",
              height: "1.125em",
              margin: ".3em"
            }}
            isLoading
          />
          {pingStartTime && (
            <ElapsedTimerDisplay
              key={`elapsed-timer-cell-${cellId}`}
              startTime={pingStartTime}
              maxDurationSec={maxTimeToWaitForPingEventSec}
              renderElapsedTime={renderPingElapsedTime}
              onTimeout={handlePingTestTimeout}
            />
          )}
        </PTLTotePlacementStyled>
      ) : (
        <PTLTotePlacementStyled
          celltype={cellData?.type ?? null}
          sx={{
            ...(ptlStatus && {
              backgroundColor: (theme) => theme.palette[ptlStatus].light
            })
          }}
          role="gridcell"
          aria-label={`${cellData?.type ?? "empty"} cell`}
        >
          {isPortCell && (
            <Stack
              flexDirection="column"
              alignItems="center"
              gap={0.5}
              sx={{
                gridColumn: "1/3",
                gridRow: "1/3",
                justifySelf: "center"
              }}
              role="note"
              aria-label="port cell content"
            >
              <Workstation24Px
                style={{
                  height: 70,
                  width: 70,
                  fill: basicColours.mono.white
                }}
              />
              <Typography
                variant="body1"
                color="white"
                sx={{
                  textAlign: "center"
                }}
              >
                {t("port id")}
                {": "}
                <span
                  style={{
                    fontWeight: 600
                  }}
                >
                  {portId}
                </span>
              </Typography>
            </Stack>
          )}

          {isToteCell && (
            <Stack
              flexDirection="column"
              gap={0.5}
              justifyContent={"space-between"}
              width="100%"
              sx={{
                gridColumn: "span 2",
                gridRow: "1/3"
              }}
            >
              <Stack
                flexDirection="row"
                justifyContent={"space-between"}
                width="100%"
                alignSelf={ptlStatus ? "self-start" : "self-end"}
                margin={0}
                alignItems="center"
              >
                <Typography
                  variant="h5"
                  color={ptlStatus || "textSecondary"}
                  fontWeight={400}
                  sx={{
                    alignSelf: ptlStatus ? "self-start" : "self-end"
                  }}
                  role="note"
                  aria-label="tote position"
                >
                  {`P${totePosition}`}
                </Typography>
                {ptlStatus === "error" && (
                  <Box>
                    <AnimatedX
                      color={mainColours.background.error.primary}
                      size={30}
                    />
                  </Box>
                )}
              </Stack>
              {ptlCellStatus && (
                <>
                  {ptlStatus !== "success" && (
                    <Button
                      variant="contained"
                      color="primary"
                      sx={{
                        minWidth: "140px",
                        maxHeight: "35px",
                        fontSize: "1rem",
                        alignSelf: "center"
                      }}
                      loading={isLoading}
                      disabled={ptlCellStatus.requested}
                      onClick={() => {
                        if (!putToLightModuleId) return;
                        void handleStartPtlTest(putToLightModuleId);
                      }}
                      startIcon={
                        ptlStatus === "error" ? (
                          <ReplayIcon
                            sx={{
                              color: basicColours.mono.white,
                              width: 20,
                              height: 20
                            }}
                          />
                        ) : undefined
                      }
                    >
                      {ptlStatus === "error" ? t("retry") : t("test")}
                    </Button>
                  )}
                  {ptlStatus === "success" && (
                    <AnimatedCheckMark
                      checkColor={mainColours.background.success.primary}
                      size={50}
                    />
                  )}
                </>
              )}
              <Typography
                color="textSecondary"
                variant="subtitle1"
                sx={{
                  alignSelf: "self-start",
                  marginBottom: "2px",
                  lineHeight: "1.125rem"
                }}
                role="note"
                aria-label="ptl id"
              >
                {`${t("ptl id")}: `}
                <span
                  style={{
                    fontWeight: 600,
                    color: mainColours.text.primary
                  }}
                >
                  {putToLightModuleId}
                </span>
              </Typography>
            </Stack>
          )}
          <Typography
            variant="body2"
            color={
              cellData?.type === "port"
                ? basicColours.mono.white
                : mainColours.text.primary
            }
            sx={{
              alignSelf: "self-end",
              textAlign: "end",
              gridColumn: "2/3",
              gridRow: "3/4",
              ...((isEmptyCell || isPortCell) && {
                gridColumn: "1/3",
                textAlign: "center"
              })
            }}
            role="note"
            aria-label="cell coordinates"
          >
            X
            <span
              style={{
                fontWeight: 600
              }}
            >
              {x}
            </span>
            {", "}Y
            <span
              style={{
                fontWeight: 600
              }}
            >
              {y}
            </span>
          </Typography>
        </PTLTotePlacementStyled>
      )}
      {showSimulateButton && (
        <Button
          variant="contained"
          color="secondary"
          sx={{
            maxHeight: "35px",
            padding: "6px 10px",
            fontSize: ".85rem",
            lineHeight: "1rem",
            alignSelf: "center",
            borderRadius: "8px"
          }}
          loading={isSimulatePtlBtnLoading}
          disabled={!ptlCellStatus.requested}
          onClick={() => {
            if (!cellData?.putToLightModuleId) return;
            void simulatePTLButtonPress(cellData.putToLightModuleId);
          }}
        >
          {t("ptl simulate button press")}
        </Button>
      )}
    </Box>
  );
};

export default memo(
  WorkstationGridCell,
  (prev, next) => prev.cellId === next.cellId
);
